import React, { useMemo } from "react";
import type { OptionNameValuePair } from "../../../../../lib/interfaces/types";
import { cn } from "../../../../../lib/utils";
import DateCell from "../../../../../components/ui/baseDataTable/cells/DateCell";
import NumberCell from "../../../../../components/ui/baseDataTable/cells/NumberCell";
import SelectCell from "../../../../../components/ui/baseDataTable/cells/SelectCell";
import TextCell from "../../../../../components/ui/baseDataTable/cells/TextCell";
import type { ColumnType, IColumnConfig, IDataRow, TextCellProps } from "../../../../../components/ui/baseDataTable";

type SingleRowData = {
  particulars: string;
  c1: unknown;
  c2: unknown;
  c3: unknown;
};

export type FmrSingleRowColumnConfig = {
  headerName: string;
  type: Extract<ColumnType, "text" | "select" | "number" | "date">;
  value: unknown;
  onChange: (v: unknown) => void;
  isEditable?: IColumnConfig<SingleRowData>["isEditable"];
  options?: OptionNameValuePair<unknown>[];
  textCellProps?: Partial<TextCellProps>;
  headerClassName?: string;
  colWidth?: string;
  minColWidth?: string;
};

export interface FmrSingleRowGridTableProps {
  title: string;
  columns: [FmrSingleRowColumnConfig, FmrSingleRowColumnConfig, FmrSingleRowColumnConfig];
  particularsValue?: string;
  particularsColWidth?: string;
  particularsHeaderClassName?: string;
}

const headerClass =
  "bg-[#F1F5F9] box-border min-h-[56px] p-2 text-left font-sans font-semibold text-xs leading-[1.125rem] text-[#374151] align-middle whitespace-normal";

const cellClass = "p-2 relative bg-white";

function renderFmrHeaderTitle(headerName: string): React.ReactNode {
  if (!headerName.includes("**")) return headerName;
  const parts = headerName.split("**");
  return (
    <span className="inline-flex flex-wrap items-center align-middle">
      {parts.map((part, idx) => (
        <span key={idx} className="inline-flex flex-wrap items-center">
          {part}
          {idx < parts.length - 1 ? <span className="text-[#DC2626] font-medium">**</span> : null}
        </span>
      ))}
    </span>
  );
}

function resolveEditable(cfg: FmrSingleRowColumnConfig, rowData: SingleRowData): boolean {
  if (cfg.isEditable === undefined) return true;
  if (typeof cfg.isEditable === "function") {
    const dataRow: IDataRow<SingleRowData> = { data: rowData, isEditable: true };
    return cfg.isEditable(0, rowData, dataRow);
  }
  return cfg.isEditable;
}

export const FmrSingleRowGridTable: React.FC<FmrSingleRowGridTableProps> = ({
  title,
  columns,
  particularsValue = "Details",
  particularsColWidth = "253px",
  particularsHeaderClassName,
}) => {
  const rowData: SingleRowData = useMemo(
    () => ({
      particulars: particularsValue,
      c1: columns[0].value,
      c2: columns[1].value,
      c3: columns[2].value,
    }),
    [columns, particularsValue],
  );

  const baseTextCellProps: TextCellProps = {
    className: "w-full min-w-0 px-0 overflow-hidden",
    numberOfRows: 1,
    truncateChars: 50_000,
  };

  const renderCell = (idx: 0 | 1 | 2) => {
    const cfg = columns[idx];
    const editable = resolveEditable(cfg, rowData);

    if (cfg.type === "text") {
      return (
        <TextCell
          {...baseTextCellProps}
          {...cfg.textCellProps}
          value={String(cfg.value ?? "")}
          isEditable={editable}
          onChange={(v) => cfg.onChange(v)}
        />
      );
    }

    if (cfg.type === "select") {
      const options = (cfg.options ?? []) as OptionNameValuePair<string>[];
      const valueStr = cfg.value != null ? String(cfg.value) : undefined;

      return (
        <SelectCell
          name="option"
          value={valueStr}
          isEditable={editable}
          options={options}
          onChange={(v) => cfg.onChange(v)}
          hideChevron
          rootContainerClassName="min-w-[120px]"
          labelClassName="px-0"
          dropdownClassName="w-max min-w-full [&>div:first-child]:hidden"
        />
      );
    }

    if (cfg.type === "number") {
      return (
        <NumberCell
          value={cfg.value as number | null | undefined}
          isEditable={editable}
          onChange={(v) => cfg.onChange(v)}
          className="px-0 min-w-[80px]"
        />
      );
    }

    return (
      <DateCell
        value={cfg.value != null ? String(cfg.value) : undefined}
        isEditable={editable}
        onChange={(v) => cfg.onChange(v)}
        className="px-0 min-w-[110px]"
        inputClassName="bg-transparent"
      />
    );
  };

  const particularsStyle = (): React.CSSProperties => ({
    width: particularsColWidth,
    minWidth: particularsColWidth,
  });

  const dataColStyle = (cfg: FmrSingleRowColumnConfig): React.CSSProperties | undefined => {
    if (!cfg.colWidth) return undefined;
    return {
      width: cfg.colWidth,
      minWidth: cfg.minColWidth ?? "85px",
    };
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-sans text-xs font-medium leading-none text-[#6B7280]">{title}</span>
      <div className="w-full relative border-[1px] border-[#E5E5E5] rounded-md min-w-0 overflow-x-auto overflow-y-hidden">
        <div className="relative w-max min-w-full max-w-none overflow-hidden rounded-lg">
          <table className="w-max min-w-full border-collapse">
            <colgroup>
              <col style={particularsStyle()} />
              <col style={dataColStyle(columns[0])} />
              <col style={dataColStyle(columns[1])} />
              <col style={dataColStyle(columns[2])} />
            </colgroup>
            <thead>
              <tr>
                <th
                  className={cn(headerClass, "rounded-tl-lg", particularsHeaderClassName)}
                  style={particularsStyle()}
                >
                  Particulars
                </th>
                <th className={cn(headerClass, columns[0].headerClassName)} style={dataColStyle(columns[0])}>
                  {renderFmrHeaderTitle(columns[0].headerName)}
                </th>
                <th className={cn(headerClass, columns[1].headerClassName)} style={dataColStyle(columns[1])}>
                  {renderFmrHeaderTitle(columns[1].headerName)}
                </th>
                <th
                  className={cn(headerClass, columns[2].headerClassName, "rounded-tr-none")}
                  style={dataColStyle(columns[2])}
                >
                  {renderFmrHeaderTitle(columns[2].headerName)}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  className={cn(
                    cellClass,
                    "bg-[var(--tailwind-colors-gray-100,#F3F4F6)] rounded-bl-lg",
                    particularsHeaderClassName,
                  )}
                  style={particularsStyle()}
                >
                  <span className="font-sans text-sm font-normal leading-[1.25rem] text-[#0A0A0A]">
                    {particularsValue}
                  </span>
                </td>
                <td className={cn(cellClass, columns[0].headerClassName)} style={dataColStyle(columns[0])}>
                  {renderCell(0)}
                </td>
                <td className={cn(cellClass, columns[1].headerClassName)} style={dataColStyle(columns[1])}>
                  {renderCell(1)}
                </td>
                <td
                  className={cn(cellClass, columns[2].headerClassName, "rounded-br-lg")}
                  style={dataColStyle(columns[2])}
                >
                  {renderCell(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

FmrSingleRowGridTable.displayName = "FmrSingleRowGridTable";
